import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styles from 'components/TabSelector/TabSelector.scss';

export const TabSelector = ({ className, tabs = [], defaultActiveIndex = 0, onTabChange, resetTrigger }) => {
  const [activeIndex, setActiveIndex] = useState(Math.min(defaultActiveIndex, tabs.length - 1));
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const buttonRefs = useRef([]);
  const containerRef = useRef(null);

  useEffect(() => {
    setActiveIndex(Math.min(defaultActiveIndex, tabs.length - 1));
  }, [resetTrigger]);

  const updateIndicator = (target) => {
    if (!target || !containerRef.current) return;

    const containerLeft = containerRef.current.getBoundingClientRect().left;
    const targetLeft = target.getBoundingClientRect().left;
    const left = targetLeft - containerLeft;

    setIndicatorStyle({
      left,
      width: target.offsetWidth,
    });
  };

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, tabs.length - 1));
  }, [tabs.length]);

  useEffect(() => {
    setActiveIndex(Math.min(defaultActiveIndex, tabs.length - 1));
  }, [defaultActiveIndex]);

  useEffect(() => {
    const activeButton = buttonRefs.current[activeIndex];
    if (activeButton) {
      updateIndicator(activeButton);
    }
  }, [tabs, activeIndex]);

  const handleTabClick = (index, e) => {
    setActiveIndex(index);
    onTabChange?.(index);
    updateIndicator(e.currentTarget);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTabClick(index, e);
      e.target.blur();
    }

    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const direction = e.key === 'ArrowRight' ? 1 : -1;
      const newIndex = (index + direction + tabs.length) % tabs.length;
      buttonRefs.current[newIndex]?.focus();
    }
  };

  return (
    <div className={cn(styles.tabSelectorContainer, className)} ref={containerRef} role='tablist'>
      <div className={styles.tabButtons}>
        {tabs.map((tabLabel, index) => {
          const isActive = activeIndex === index;

          return (
            <button
              ref={(el) => (buttonRefs.current[index] = el)}
              key={index}
              className={cn(styles.tabButton, {
                [styles.active]: isActive,
              })}
              onClick={(e) => handleTabClick(index, e)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              role='tab'
              aria-selected={isActive}
              aria-controls={`tab-content-${index}`}
              id={`tab-${index}`}
              tabIndex={isActive ? -1 : 0}
              type='button'
            >
              {tabLabel}
            </button>
          );
        })}
        <div
          className={styles.indicator}
          style={{
            transform: `translateX(${indicatorStyle.left}px)`,
            width: `${indicatorStyle.width}px`,
          }}
        />
      </div>
    </div>
  );
};
